'use client';

import { Button } from '@magicschool/spellbook/components/Button';
import { Pagination } from '@magicschool/spellbook/components/Pagination';
import { Select } from '@magicschool/spellbook/components/Select';
import { Table } from '@magicschool/spellbook/components/Table';
import { Text } from '@magicschool/spellbook/components/Text';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { cn } from '@magicschool/spellbook/lib/utils';
import {
  type ColumnFiltersState,
  type ExpandedState,
  type FilterFn,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type SortingState,
  type Table as TableInstance,
  type Updater,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { CONTROLS_COLUMN_ID, EMPTY_COLUMN_FILTERS, EMPTY_COLUMN_VISIBILITY, EMPTY_SORTING } from './constants';
import { DataGridProvider, useDataGridProviderValue } from './DataGridContext';
import { DataGridFilters } from './DataGridFilters';
import { ExpandRowTrigger } from './ExpandRowTrigger';
import { SelectRowCheckbox } from './SelectRowCheckbox';
import type { DataGridProps, DataGridRowSelectionChangeOptions, DataGridRowSelectionState } from './types';
import { useDataGridPagination } from './useDataGridPagination';
import { useDataGridSearch } from './useDataGridSearch';
import { useDataGridSelection } from './useDataGridSelection';
import { useDataGridText } from './useDataGridText';
import { defaultFilterFn, updateColumnFilters } from './utils';

/**
 * An interactive data table with built-in sorting, filtering, search, pagination, and expandable rows. Built on TanStack React Table.
 *
 * ### When to use
 * - Tabular data that users need to sort, filter, search, or paginate
 * - Admin tables, submission lists, user management, and similar CRUD views
 * - Server-side paginated data sets that are too large to load at once
 * - Tables where rows can expand to reveal additional detail
 *
 * ### When NOT to use
 * - For static, display-only tabular data with no interactions: use `Table` instead
 * - For simple lists of items (non-tabular): use a list or card layout instead
 * - For key-value pair display: use a description list instead
 *
 * ### DataGrid vs Table
 * | Feature | DataGrid | Table |
 * |---------|----------|-------|
 * | Sorting | Built-in | Manual |
 * | Filtering | Built-in (column + global) | None |
 * | Search | Built-in with debounce | None |
 * | Pagination | Built-in (client + server) | None |
 * | Expandable rows | Built-in | None |
 * | TanStack Table | Integrated | Not used |
 * | Use case | Interactive data | Static display |
 *
 * ### API
 * - `data` / `columns`: The data array and TanStack column definitions (required)
 * - `serverSide`: Enables server-side sorting, filtering, and pagination. Pair with `totalRowCount` and the `on*Change` callbacks.
 * - `filters`: Array of filter chip configs (`select`, `toggle`, `date`) rendered above the table
 * - `text`: All user-facing strings for i18n. Supports static strings and dynamic text functions.
 * - `refs`: Forward a ref to the underlying `<table>` element via `refs.table`
 * - `renderCustomToolbar` / `renderCustomFilters`: Escape hatches for custom toolbar or filter UI
 * - `tableActions`: Array of `<Table.Action>` elements for table-level actions (e.g., export). Use `useDataGridContext()` inside action components.
 *
 * ### Column definitions (TanStack React Table)
 * Columns follow the TanStack `ColumnDef` interface. Use `meta` for Spellbook-specific options:
 * - `meta.cellVariant`: `'text'` (default), `'number'`, `'component'`, or `'empty'`
 * - `meta.wrapText`: Whether cell text wraps (default `true`)
 * - `meta.align`: Column alignment (`'left'`, `'center'`, `'right'`)
 * - `meta.headerClassName` / `meta.cellClassName`: Additional CSS classes
 *
 * Buttons inside a `cell` renderer are `sm` with the `default` shape, neutral `ghost` by default, primary `muted` only
 * for a row's call to action, and never bordered. The "Conventions/Buttons in tables" docs page is the full rule.
 *
 * An empty cell shows an em dash in the border color automatically: return `null`, `undefined`, or `''` from the cell, never a
 * placeholder like `'—'`, `'-'`, or `'N/A'`. It applies to `text`/`number` cells; a `component` cell opts in with
 * `meta.emptyCell: true`. Pass `text.emptyCellLabel` to translate its screen reader label.
 *
 * ### Client-side vs server-side
 * - **Client-side** (default): Pass all data upfront. Sorting, filtering, search, and pagination are handled internally.
 * - **Server-side** (`serverSide`): Pass the current page of data. Handle state externally via `onPaginationChange`, `onSortingChange`, `onSearchChange`, and `onColumnFiltersChange`. Search input is debounced (500ms).
 *
 * ### Accessibility
 * - Sort buttons have accessible labels (`sortAscending`, `sortDescending`, `sortToggle` via `text`)
 * - Search input has an `aria-label` and linked live region for result announcements
 * - Pagination controls have accessible labels (`goToPage`, `goToPrevPage`, `goToNextPage` via `text`)
 * - Expandable row triggers have `aria-expanded` and `aria-controls` attributes
 * - Loading state renders an `aria-busy` status region
 * - Table is associated with its title via `aria-labelledby` when `text.title` is provided
 *
 * ### Example: Client-side with column definitions
 * ```tsx
 * type Product = { id: string; name: string; price: number; status: 'active' | 'draft' };
 *
 * const columns: DataGridColumn<Product>[] = [
 *   { accessorKey: 'name', header: 'Name', enableSorting: true },
 *   { accessorKey: 'price', header: 'Price', meta: { cellVariant: 'number' } },
 *   {
 *     accessorKey: 'status',
 *     header: 'Status',
 *     cell: (info) => <Badge variant={info.getValue() === 'active' ? 'success' : 'error'}>{info.getValue()}</Badge>,
 *     meta: { cellVariant: 'component' },
 *   },
 *   {
 *     id: 'actions',
 *     header: 'Actions',
 *     enableSorting: false,
 *     cell: ({ row }) => <RowActions item={row.original} />,
 *     meta: { cellVariant: 'component', headerLabelClassName: 'sr-only' },
 *   },
 * ];
 *
 * <DataGrid data={products} columns={columns} pageSize={10} text={dataGridText} />
 * ```
 *
 * ### Example: Server-side with external state
 * Pass the current page of data and wire up state callbacks for pagination, sorting, and search.
 * ```tsx
 * <DataGrid
 *   serverSide
 *   data={page.rows}
 *   columns={columns}
 *   totalRowCount={page.totalCount}
 *   isLoading={isLoading}
 *   initialPagination={pagination}
 *   initialSorting={sorting}
 *   initialSearch={search}
 *   onPaginationChange={setPagination}
 *   onSortingChange={setSorting}
 *   onSearchChange={setSearch}
 *   pageSizeOptions={[10, 20, 50, 100]}
 *   text={{ ...dataGridText, title: `Items (${page.totalCount})` }}
 * />
 * ```
 *
 * ### Example: With filters and table actions
 * Combine column filters (select, toggle, date) with table-level action buttons.
 * ```tsx
 * <DataGrid
 *   data={items}
 *   columns={columns}
 *   filters={[
 *     { type: 'select', id: 'category', label: 'Category', multiple: true, options: categoryOptions },
 *     { type: 'toggle', id: 'featured', label: 'Featured Only' },
 *     { type: 'date', id: 'createdAfter', label: 'Created After' },
 *   ]}
 *   onColumnFiltersChange={setColumnFilters}
 *   onClearAllFilters={resetFilters}
 *   tableActions={[<ExportButton key="export" />]}
 *   text={dataGridText}
 * />
 * ```
 *
 * ### Example: With expandable rows
 * ```tsx
 * <DataGrid
 *   data={items}
 *   columns={columns}
 *   getRowCanExpand={(row) => row.original.hasDetails}
 *   getExpandedRowContent={({ row }) => <DetailPanel data={row.original} />}
 *   text={dataGridText}
 * />
 * ```
 *
 * ### Example: With table ref for focus management
 * Forward a ref to the underlying `<table>` element for programmatic focus control.
 * ```tsx
 * const tableRef = useRef<HTMLTableElement>(null);
 *
 * <DataGrid data={items} columns={columns} refs={{ table: tableRef }} text={dataGridText} />
 * ```
 *
 * ### Example: Column with max-width and truncation
 * Constrain column width and disable wrapping so long text content is truncated with an ellipsis.
 * ```tsx
 * const columns: DataGridColumn<Row>[] = [
 *   {
 *     accessorKey: 'summary',
 *     header: 'Summary',
 *     minSize: 300,
 *     enableSorting: false,
 *     meta: {
 *       cellVariant: 'text',
 *       wrapText: false,
 *       cellClassName: 'max-w-96',
 *     },
 *   },
 *   {
 *     accessorKey: 'preview',
 *     header: 'Preview',
 *     meta: {
 *       cellVariant: 'component',
 *       cellClassName: 'min-w-[200px] max-w-[500px]',
 *     },
 *   },
 * ];
 * ```
 */

export function DataGrid<TData = unknown>({
  data,
  columns,
  tableActions,
  bulkActions,
  hideSearch = false,
  searchLeadingAdornment,
  hidePagination = false,
  pageSize = 10,
  pageSizeOptions = [5, 10, 20, 50],
  className,
  'data-testid': dataTestId,
  rowClassName,
  onRowClick,
  enableSorting = true,
  enableFiltering = true,
  initialSearch = '',
  initialPagination,
  initialSorting = EMPTY_SORTING,
  initialColumnFilters = EMPTY_COLUMN_FILTERS,
  initialColumnVisibility = EMPTY_COLUMN_VISIBILITY,
  initialExpanded = {},
  globalFilterFn = defaultFilterFn as FilterFn<TData>,
  renderCustomToolbar,
  renderCustomFilters,
  filters = [],
  serverSide = false,
  totalRowCount,
  onPaginationChange,
  onSortingChange,
  onSearchChange,
  onClearAllFilters,
  onColumnFiltersChange,
  isLoading = false,
  text = {},
  refs,
  getRowCanExpand,
  getExpandedRowContent,
  expandedRowCellClassName,
  onExpandedChange,
  enableRowSelection = false,
  initialRowSelection,
  onRowSelectionChange,
  getRowCanSelect,
  getSelectableSubItems,
  syncParentRowSelection = true,
  preserveRowSelectionOnFilter = false,
  getRowId,
  titleElement,
  onAnalyticsEvent,
}: DataGridProps<TData>) {
  const {
    title,
    searchPlaceholder,
    searchLabel,
    clearSearch,
    noRecordsFound,
    emptyCellLabel,
    previous,
    next,
    sortAscending,
    sortDescending,
    sortToggle,
    pageSelectLabel,
    goToPage,
    goToPrevPage,
    goToNextPage,
    loading,
    filtersLabel,
    clearAllFilters,
    expandableHeaderLabel,
    selectAllRowsLabel,
    clearSelectionLabel,
    tableActionsLabel,
    selectionBarLabel,
    resolveRowsPerPage,
    resolvePageInfo,
    resolveResultsFoundAnnouncement,
    resolveShowingItemsAnnouncement,
    resolveSelectRowLabel,
    resolveExpandCollapsedLabel,
    resolveExpandExpandedLabel,
    resolveSelectedCountLabel,
    resolveSelectionOpenedLabel,
  } = useDataGridText<TData>(text);

  const announcementId = useId();
  const selectionBarLabelId = useId();
  const isServerSide = Boolean(serverSide);
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialColumnVisibility);
  const [pagination, setPagination] = useState<PaginationState>(initialPagination ?? { pageIndex: 0, pageSize });
  const [expanded, setExpanded] = useState<ExpandedState>(initialExpanded);
  const internalTableRef = useRef<HTMLTableElement>(null);
  const tableTitleRef = useRef<HTMLHeadingElement>(null);
  const tableInstanceRef = useRef<TableInstance<TData> | null>(null);

  // Sync external ref with internal ref
  useImperativeHandle(refs?.table, () => internalTableRef.current as HTMLTableElement);

  // Generate unique ID for table title (for aria-labelledby)
  const tableTitleId = useId();

  const resetPagination = useCallback(() => {
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  }, [pagination.pageSize]);

  const analyticsOnSearchChange = useCallback(
    (value: string) => {
      if (value) onAnalyticsEvent?.({ type: 'searched', term: value });
      onSearchChange?.(value);
    },
    [onAnalyticsEvent, onSearchChange],
  );

  const { search, searchInput, searchInputRef, handleSearchInputChange, handleSearchKeyDown, handleClearSearch, setSearch } =
    useDataGridSearch({
      initialSearch,
      isServerSide,
      onSearchChange: analyticsOnSearchChange,
      resetPagination,
    });

  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      const firstSort = newSorting[0];
      if (firstSort) {
        onAnalyticsEvent?.({ type: 'sorted', column: firstSort.id, direction: firstSort.desc ? 'desc' : 'asc' });
      }
      if (isServerSide) {
        onSortingChange?.(newSorting);
      }
    },
    [sorting, isServerSide, onSortingChange, onAnalyticsEvent],
  );

  const handleColumnFiltersChange = useCallback(
    (updater: Updater<ColumnFiltersState>) => {
      const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
      setColumnFilters(newFilters);
      onColumnFiltersChange?.(newFilters);
      if (onAnalyticsEvent) {
        for (const filter of newFilters) {
          const prevFilter = columnFilters.find((f) => f.id === filter.id);
          if (!prevFilter || JSON.stringify(prevFilter.value) !== JSON.stringify(filter.value)) {
            const value = Array.isArray(filter.value) ? filter.value.map(String) : [String(filter.value)];
            onAnalyticsEvent({ type: 'filtered', column: filter.id, value });
          }
        }
      }
      setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    },
    [columnFilters, onColumnFiltersChange, onAnalyticsEvent, pagination.pageSize],
  );

  const handlePaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newPagination);

      if (isServerSide) {
        onPaginationChange?.(newPagination);
      }
    },
    [pagination, isServerSide, onPaginationChange],
  );

  const handleClearAllFilters = useCallback(() => {
    onClearAllFilters?.();
    setColumnFilters([]);
    onColumnFiltersChange?.([]);
    onAnalyticsEvent?.({ type: 'filters_cleared' });
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  }, [pagination.pageSize, onClearAllFilters, onColumnFiltersChange, onAnalyticsEvent]);

  const handleFilterChange = useCallback(
    (filterId: string, value: unknown) => {
      const newFilters = updateColumnFilters(columnFilters, filterId, value);
      handleColumnFiltersChange(newFilters);
    },
    [columnFilters, handleColumnFiltersChange],
  );

  const getExpandedContentId = useCallback((row: Row<TData>) => `expanded-${row.id}`, []);

  const handleExpandedChange = (updater: Updater<ExpandedState>) => {
    if (onAnalyticsEvent) {
      const newExpanded = typeof updater === 'function' ? updater(expanded) : updater;
      if (typeof expanded !== 'boolean' && typeof newExpanded !== 'boolean') {
        const allRowIds = new Set([...Object.keys(expanded), ...Object.keys(newExpanded)]);
        for (const rowId of allRowIds) {
          const wasExpanded = Boolean((expanded as Record<string, boolean>)[rowId]);
          const isExpanded = Boolean((newExpanded as Record<string, boolean>)[rowId]);
          if (wasExpanded !== isExpanded) {
            onAnalyticsEvent({ type: 'row_expansion_changed', rowId, action: isExpanded ? 'expanded' : 'collapsed' });
          }
        }
      }
    }
    setExpanded(updater);
    onExpandedChange?.(updater);
  };

  const prevSelectedCountRef = useRef(0);

  const analyticsOnRowSelectionChange = useCallback(
    (selection: DataGridRowSelectionState, options: DataGridRowSelectionChangeOptions) => {
      if (onAnalyticsEvent) {
        const selectedCount = Object.values(selection.rows).filter(Boolean).length;
        if (options.source === 'filter') {
          // Filter/search auto-clears selection — reset baseline so the next genuine selection
          // computes selectionAction against 0, not the pre-clear count.
          prevSelectedCountRef.current = selectedCount;
        } else {
          // 'clear' and 'expanded-sub-item' sources fold into 'individual' — both represent
          // single-row-level interactions with no separate enum value in the event schema.
          const selectionType = options.source === 'header' ? 'header' : 'individual';
          const selectionAction = selectedCount > prevSelectedCountRef.current ? 'selected' : 'deselected';
          prevSelectedCountRef.current = selectedCount;
          onAnalyticsEvent({ type: 'row_selection_changed', selectedCount, selectionType, selectionAction });
        }
      }
      onRowSelectionChange?.(selection, options);
    },
    [onAnalyticsEvent, onRowSelectionChange],
  );

  const {
    rowSelection,
    expandedSubItemSelection,
    selectionAnnouncement,
    selectionSourceRef,
    handleClearSelection,
    handleRowSelectionChange,
    createExpandedRowContentOptions,
    hasSelection,
  } = useDataGridSelection<TData>({
    enableRowSelection,
    initialRowSelection,
    getSelectableSubItems,
    syncParentRowSelection,
    preserveRowSelectionOnFilter,
    onRowSelectionChange: analyticsOnRowSelectionChange,
    search,
    columnFilters,
    isLoading,
    resolveSelectedCountLabel,
    resolveSelectionOpenedLabel,
    internalTableRef,
    tableInstanceRef,
  });

  // Column definitions read all dynamic values (selection state, labels, loading) from `table.options.meta.dataGrid`
  // at render time instead of closing over them. This keeps `tableColumns` stable so TanStack doesn't rebuild
  // its internal column/row model on every selection change.
  const tableColumns = useMemo(() => {
    const cols = [...columns];

    const canExpand = getRowCanExpand && getExpandedRowContent;

    const selectHeader = enableRowSelection
      ? ({ table }: { table: TableInstance<TData> }) => {
          const meta = table.options.meta?.dataGrid;
          if (!meta) return null;
          const hasSubSel = Object.values(meta.expandedSubItemSelection).some((subs) => Object.values(subs).some(Boolean));
          return (
            <SelectRowCheckbox
              label={meta.selectAllRowsLabel}
              checked={table.getIsAllPageRowsSelected()}
              indeterminate={table.getIsSomePageRowsSelected() || (!table.getIsAllPageRowsSelected() && hasSubSel)}
              onToggle={() => {
                selectionSourceRef.current = 'header';
                table.toggleAllPageRowsSelected();
              }}
              disabled={meta.isLoading}
            />
          );
        }
      : undefined;

    const renderSelectCell = (row: Row<TData>, table: TableInstance<TData>) => {
      const meta = table.options.meta?.dataGrid;
      if (!meta) return null;
      const canSelect = meta.getRowCanSelect?.(row) ?? true;
      if (!canSelect) return null;

      let indeterminate = false;
      if (meta.getSelectableSubItems) {
        const subItems = meta.getSelectableSubItems(row);
        if (subItems.length > 0) {
          const selectedCount = Object.values(meta.expandedSubItemSelection[row.id] ?? {}).filter(Boolean).length;
          const isParentSelected = row.getIsSelected();
          if (isParentSelected) {
            // Parent is checked: show indeterminate only if some (not all) sub-items are selected
            indeterminate = selectedCount > 0 && selectedCount < subItems.length;
          } else {
            // Parent is not checked: show indeterminate if any sub-items are selected
            indeterminate = selectedCount > 0;
          }
        }
      }

      return (
        <SelectRowCheckbox
          label={meta.resolveSelectRowLabel(row.original)}
          checked={row.getIsSelected()}
          indeterminate={indeterminate}
          onToggle={() => {
            selectionSourceRef.current = 'row';
            row.toggleSelected();
          }}
          disabled={meta.isLoading}
        />
      );
    };

    if (enableRowSelection || canExpand) {
      cols.unshift({
        id: CONTROLS_COLUMN_ID,
        enableSorting: false,
        ...(canExpand ? { accessorKey: expandableHeaderLabel } : {}),
        header: enableRowSelection ? selectHeader : undefined,
        cell: ({ row, table }) => {
          const meta = table.options.meta?.dataGrid;
          return (
            <div className="flex items-center gap-1">
              {enableRowSelection && renderSelectCell(row, table)}
              {canExpand && row.getCanExpand() && (
                <ExpandRowTrigger
                  expandedContentId={getExpandedContentId(row)}
                  isExpanded={row.getIsExpanded()}
                  onClick={row.getToggleExpandedHandler()}
                  tooltipLabels={{
                    expandableRowTriggerExpandedLabel: meta?.resolveExpandableRowTriggerExpandedLabel(row.original) ?? '',
                    expandableRowTriggerCollapsedLabel: meta?.resolveExpandableRowTriggerCollapsedLabel(row.original) ?? '',
                  }}
                />
              )}
            </div>
          );
        },
        meta: {
          cellVariant: 'component',
          ...(!enableRowSelection && canExpand ? { headerLabelClassName: 'sr-only' } : {}),
          headerWrapperClassName: 'w-0',
        },
      });
    }

    return cols;
  }, [
    columns,
    enableRowSelection,
    getRowCanExpand,
    getExpandedRowContent,
    expandableHeaderLabel,
    selectionSourceRef,
    getExpandedContentId,
  ]);

  const table = useReactTable<TData>({
    data,
    columns: tableColumns,
    getRowId,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      globalFilter: search,
      pagination,
      expanded,
      rowSelection,
    },
    meta: {
      dataGrid: {
        expandedSubItemSelection,
        getSelectableSubItems,
        getRowCanSelect,
        resolveSelectRowLabel,
        selectAllRowsLabel,
        isLoading,
        resolveExpandableRowTriggerExpandedLabel: resolveExpandExpandedLabel,
        resolveExpandableRowTriggerCollapsedLabel: resolveExpandCollapsedLabel,
      },
    },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setSearch,
    onExpandedChange: handleExpandedChange,
    onPaginationChange: handlePaginationChange,
    onRowSelectionChange: handleRowSelectionChange,
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowCanExpand,
    globalFilterFn,
    defaultColumn: {
      filterFn: defaultFilterFn as FilterFn<TData>,
    },
    manualSorting: isServerSide,
    manualFiltering: isServerSide,
    manualPagination: isServerSide,
    pageCount: isServerSide && totalRowCount ? Math.ceil(totalRowCount / pagination.pageSize) : undefined,
  });
  // Wire up table instance so handleRowSelectionChange (in useDataGridSelection) can call table.getRow()
  tableInstanceRef.current = table;

  const { rows } = table.getRowModel();

  const hasExpandableRows = useMemo(() => rows.some((row) => row.getCanExpand()), [rows]);

  // Sync external state changes for server-side scenarios
  useEffect(() => {
    if (isServerSide) {
      setSorting(initialSorting);
    }
  }, [isServerSide, initialSorting]);

  useEffect(() => {
    if (isServerSide) {
      setColumnFilters(initialColumnFilters);
    }
  }, [isServerSide, initialColumnFilters]);

  const { currentPage, totalPages, startRow, endRow, currentTotalRows, handlePageChange, handlePageSizeChange, getVisiblePages } =
    useDataGridPagination({
      table,
      pagination,
      setPagination,
      isServerSide,
      isLoading,
      totalRowCount,
      onPaginationChange,
      initialPagination,
      onAnalyticsEvent,
    });

  // Create focused announcement message for screen readers
  const announcementMessage = isLoading
    ? loading
    : currentTotalRows === 0
      ? noRecordsFound
      : search || columnFilters.length > 0
        ? resolveResultsFoundAnnouncement(currentTotalRows)
        : resolveShowingItemsAnnouncement(startRow, endRow, currentTotalRows);

  const contextValue = useDataGridProviderValue<TData>({
    table,
    isLoading,
    internalTableRef,
    tableTitleRef,
    expandedSubItemSelection,
    getSelectableSubItems,
  });

  return (
    <DataGridProvider value={contextValue}>
      <div data-spellbook-name="data-grid" className={className} data-testid={dataTestId}>
        {renderCustomToolbar && <div className="flex items-center justify-between gap-2 mb-2">{renderCustomToolbar(table)}</div>}

        <div className="relative">
          <Table fullWidth isLoading={isLoading} text={{ loading, emptyCellLabel }}>
            {title && (
              <Table.Title
                id={tableTitleId}
                ref={tableTitleRef}
                tabIndex={-1}
                className="!mb-2 outline-none"
                {...(titleElement ? { element: titleElement } : {})}
              >
                {title}
              </Table.Title>
            )}

            {/* Filters — omit slot when empty so Table does not render table-heading-wrapper */}
            {(filters.length > 0 || renderCustomFilters) && (
              <Table.FilterChips>
                {renderCustomFilters ? (
                  renderCustomFilters({ table, searchInputRef })
                ) : (
                  <DataGridFilters
                    filters={filters}
                    columnFilters={columnFilters}
                    isLoading={isLoading}
                    filtersLabel={filtersLabel}
                    clearAllFilters={clearAllFilters}
                    onFilterChange={handleFilterChange}
                    onClearAllFilters={handleClearAllFilters}
                    searchInputRef={searchInputRef}
                  />
                )}
              </Table.FilterChips>
            )}

            {/* Search input */}
            {!hideSearch && (
              <Table.SearchInput
                ref={searchInputRef}
                placeholder={searchPlaceholder}
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                onClearSearch={handleClearSearch}
                disabled={isLoading}
                leadingAdornment={searchLeadingAdornment}
                aria-describedby={announcementId}
                text={{
                  label: searchLabel,
                  clearSearch: clearSearch,
                }}
              />
            )}

            {tableActions && tableActions.length > 0 && <Table.Actions aria-label={tableActionsLabel}>{tableActions}</Table.Actions>}

            {hasSelection && bulkActions?.length ? (
              <Table.SelectionBar>
                <section
                  // Combine with the table title so screen readers announce e.g. "Selection, Users table",
                  // disambiguating this bar when multiple grids appear on the same page.
                  aria-labelledby={title ? `${selectionBarLabelId} ${tableTitleId}` : undefined}
                  aria-label={title ? undefined : selectionBarLabel}
                  className="flex w-full items-center justify-between rounded-lg bg-primary-muted-background px-4 py-3"
                >
                  {title && (
                    <span id={selectionBarLabelId} className="sr-only">
                      {selectionBarLabel}
                    </span>
                  )}
                  <div className="flex items-center gap-3">
                    <Text variant="label-2" aria-hidden="true">
                      {resolveSelectedCountLabel(contextValue.tableState.totalSelectedCount)}
                    </Text>
                    {bulkActions}
                  </div>
                  <Button size="sm" variant="ghost" color="neutral" onClick={handleClearSelection}>
                    {clearSelectionLabel}
                  </Button>
                </section>
              </Table.SelectionBar>
            ) : null}

            {/* Table */}
            <Table.Table ref={internalTableRef} aria-labelledby={title ? tableTitleId : undefined}>
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Row key={headerGroup.id}>
                    {headerGroup.headers.map((header) =>
                      !hasExpandableRows && !enableRowSelection && header.column.id === CONTROLS_COLUMN_ID ? null : (
                        <Table.HeadCell
                          key={header.id}
                          cellVariant={header.column.columnDef.meta?.cellVariant ?? 'text'}
                          className={cn(
                            header.column.getCanSort() && 'cursor-pointer select-none',
                            header.column.columnDef.meta?.headerClassName,
                          )}
                          wrapperClassName={header.column.columnDef.meta?.headerWrapperClassName ?? undefined}
                          align={header.column.columnDef.meta?.align ?? undefined}
                        >
                          {header.isPlaceholder ? null : (
                            <div className="flex items-center gap-2">
                              <span className={cn(header.column.columnDef.meta?.headerLabelClassName)}>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </span>
                              {header.column.getCanSort() && (
                                <Table.HeadCellSort
                                  data-testid={`sort-${header.column.id}`}
                                  direction={
                                    header.column.getIsSorted() === 'asc'
                                      ? 'asc'
                                      : header.column.getIsSorted() === 'desc'
                                        ? 'desc'
                                        : undefined
                                  }
                                  sortTextAsc={sortAscending}
                                  sortTextDesc={sortDescending}
                                  sortTextToggle={sortToggle}
                                  onClick={() => header.column.toggleSorting()}
                                />
                              )}
                            </div>
                          )}
                        </Table.HeadCell>
                      ),
                    )}
                  </Table.Row>
                ))}
              </Table.Head>

              <Table.Body>
                {table.getRowModel().rows.length === 0 ? (
                  <Table.Row className={rowClassName}>
                    <Table.Cell colSpan={tableColumns.length} cellVariant="empty">
                      {typeof noRecordsFound === 'string' ? (
                        <Text variant="heading-4" element="p" role="status" aria-live="polite">
                          {noRecordsFound}
                        </Text>
                      ) : (
                        <div role="status" aria-live="polite">
                          {noRecordsFound}
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <Fragment key={row.id}>
                      <Table.Row
                        onClick={onRowClick ? () => onRowClick(row.original as TData) : undefined}
                        className={cn(rowClassName, onRowClick && 'cursor-pointer hover:bg-surface-background-1')}
                        aria-selected={enableRowSelection && row.getIsSelected() ? true : undefined}
                      >
                        {row.getVisibleCells().map((cell) =>
                          !hasExpandableRows && !enableRowSelection && cell.column.id === CONTROLS_COLUMN_ID ? null : (
                            <Table.Cell
                              key={cell.id}
                              wrapText={cell.column.columnDef.meta?.wrapText ?? true}
                              cellVariant={cell.column.columnDef.meta?.cellVariant ?? 'text'}
                              className={cell.column.columnDef.meta?.cellClassName}
                              align={cell.column.columnDef.meta?.align ?? undefined}
                              emptyCell={cell.column.columnDef.meta?.emptyCell}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Table.Cell>
                          ),
                        )}
                      </Table.Row>
                      {row.getIsExpanded() && getExpandedRowContent && (
                        <>
                          {/* HACK: to ensure expanded row content is the same color when banded */}
                          <Table.Row key={`expanded-hidden-${row.id}`} className="hidden" />
                          <Table.Row key={getExpandedContentId(row)} id={getExpandedContentId(row)}>
                            <Table.Cell
                              cellVariant="component"
                              className={cn('max-w-full', expandedRowCellClassName)}
                              colSpan={row.getAllCells().length}
                            >
                              {getExpandedRowContent({ row, options: createExpandedRowContentOptions(row) })}
                            </Table.Cell>
                          </Table.Row>
                        </>
                      )}
                    </Fragment>
                  ))
                )}
              </Table.Body>
            </Table.Table>
            {!hidePagination && !isLoading && table.getRowModel().rows.length > 0 && (
              <>
                <Table.PaginationInfo>
                  <Text variant="label-1" className="shrink-0">
                    {resolvePageInfo(startRow, endRow, currentTotalRows)}
                  </Text>
                  <Select
                    value={pagination.pageSize.toString()}
                    onValueChange={handlePageSizeChange}
                    className="min-w-24 sm:min-w-24"
                    aria-label={pageSelectLabel}
                  >
                    {pageSizeOptions.map((size) => (
                      <Select.Option value={size.toString()} key={size} label={resolveRowsPerPage(size)} />
                    ))}
                  </Select>
                </Table.PaginationInfo>

                {totalPages > 1 && (
                  <Table.Pagination>
                    <Pagination
                      text={{
                        goToPrevPage,
                        goToNextPage,
                      }}
                    >
                      <Pagination.PrevButton disabled={currentPage === 1 || isLoading} onClick={() => handlePageChange(currentPage - 1)}>
                        {previous}
                      </Pagination.PrevButton>

                      {getVisiblePages().map((page, index) =>
                        page === 'ellipsis' ? (
                          <Pagination.Ellipsis key={`ellipsis-${index}`} />
                        ) : (
                          <Pagination.Button
                            key={page}
                            page={page}
                            isActive={currentPage === page}
                            disabled={isLoading}
                            onClick={() => handlePageChange(page)}
                            text={{ goToPage }}
                          >
                            {page}
                          </Pagination.Button>
                        ),
                      )}

                      <Pagination.NextButton
                        disabled={currentPage === totalPages || isLoading}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        {next}
                      </Pagination.NextButton>
                    </Pagination>
                  </Table.Pagination>
                )}
              </>
            )}
          </Table>
          {!hideSearch && (
            <span id={announcementId} aria-live="polite" aria-atomic="true" className="sr-only">
              {announcementMessage}
            </span>
          )}
          {enableRowSelection && (
            <span aria-live="polite" aria-atomic="true" className="sr-only">
              {selectionAnnouncement}
            </span>
          )}
        </div>
      </div>
    </DataGridProvider>
  );
}
